import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Receipt, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { taxAPI } from '@/services/api';

const TaxPage = () => {
  const [taxGroups, setTaxGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [formData, setFormData] = useState({
    code: '', name: '', description: '',
    slabs: [
      { tax_type: 'gst', rate: 0 },  // CGST
      { tax_type: 'gst', rate: 0 },  // SGST  
      { tax_type: 'igst', rate: 0 }  // IGST (auto-calculated)
    ]
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchTaxGroups = useCallback(async () => {
    try {
      const response = await taxAPI.list({ search });
      setTaxGroups(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch tax groups', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => { fetchTaxGroups(); }, [fetchTaxGroups]);

  const handleOpenDialog = (tax = null) => {
    if (tax) {
      setSelectedTax(tax);
      // Parse existing slabs to separate CGST, SGST, IGST
      let cgstRate = 0, sgstRate = 0, igstRate = 0;
      for (const slab of tax.slabs || []) {
        if (slab.tax_type === 'igst') {
          igstRate = slab.rate || 0;
        } else if (slab.tax_type === 'gst') {
          // First GST slab is CGST, second is SGST
          if (cgstRate === 0) {
            cgstRate = slab.rate || 0;
          } else {
            sgstRate = slab.rate || 0;
          }
        }
      }
      // If no IGST defined, auto-calculate from CGST+SGST
      if (igstRate === 0 && (cgstRate > 0 || sgstRate > 0)) {
        igstRate = cgstRate + sgstRate;
      }
      setFormData({
        code: tax.code,
        name: tax.name,
        description: tax.description || '',
        slabs: [
          { tax_type: 'gst', rate: cgstRate },
          { tax_type: 'gst', rate: sgstRate },
          { tax_type: 'igst', rate: igstRate }
        ]
      });
    } else {
      setSelectedTax(null);
      setFormData({
        code: '', name: '', description: '',
        slabs: [
          { tax_type: 'gst', rate: 0 },
          { tax_type: 'gst', rate: 0 },
          { tax_type: 'igst', rate: 0 }
        ]
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast({ title: 'Validation Error', description: 'Code and Name are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      // Filter out zero rate slabs
      const payload = {
        ...formData,
        slabs: formData.slabs.filter(s => s.rate > 0)
      };
      if (selectedTax) {
        await taxAPI.update(selectedTax.id, payload);
        toast({ title: 'Success', description: 'Tax group updated successfully' });
      } else {
        await taxAPI.create(payload);
        toast({ title: 'Success', description: 'Tax group created successfully' });
      }
      setDialogOpen(false);
      fetchTaxGroups();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save tax group', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await taxAPI.delete(selectedTax.id);
      toast({ title: 'Success', description: 'Tax group deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchTaxGroups();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete tax group', variant: 'destructive' });
    }
  };

  const updateSlabRate = (index, rate) => {
    setFormData(prev => {
      const newSlabs = prev.slabs.map((s, i) => i === index ? { ...s, rate: parseFloat(rate) || 0 } : s);
      // Auto-update IGST (index 2) when CGST or SGST changes
      if (index === 0 || index === 1) {
        const cgst = newSlabs[0]?.rate || 0;
        const sgst = newSlabs[1]?.rate || 0;
        newSlabs[2] = { tax_type: 'igst', rate: cgst + sgst };
      }
      return { ...prev, slabs: newSlabs };
    });
  };

  const getIntraStateRate = () => {
    return (formData.slabs[0]?.rate || 0) + (formData.slabs[1]?.rate || 0);
  };

  const getIGSTRate = () => {
    return formData.slabs[2]?.rate || 0;
  };

  const getTotalRate = () => {
    return getIntraStateRate(); // Display intra-state rate as total
  };

  return (
    <div className="p-6 lg:p-8">
      <Toaster />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Tax Groups (GST)</h1>
          <p className="text-gray-500">Manage GST tax configurations</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-500 hover:bg-blue-600" data-testid="add-tax-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Tax Group
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Search tax groups..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-200 text-gray-800" />
        </div>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : taxGroups.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No tax groups found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500">Code</TableHead>
                  <TableHead className="text-gray-500">Name</TableHead>
                  <TableHead className="text-gray-500">Tax Rate</TableHead>
                  <TableHead className="text-gray-500">Breakdown</TableHead>
                  <TableHead className="text-gray-500">Status</TableHead>
                  <TableHead className="text-gray-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxGroups.map(tax => (
                  <TableRow key={tax.id} className="border-gray-200 hover:bg-white" data-testid={`tax-row-${tax.code}`}>
                    <TableCell className="font-medium text-gray-800">{tax.code}</TableCell>
                    <TableCell className="text-gray-600">{tax.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Percent className="w-4 h-4 text-green-500" />
                        <span className="text-green-400 font-bold">{tax.total_rate}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {tax.slabs?.length > 0 ? (
                        tax.slabs.map((s, i) => (
                          <span key={i}>
                            {s.tax_type.toUpperCase()} {s.rate}%{i < tax.slabs.length - 1 ? ' + ' : ''}
                          </span>
                        ))
                      ) : 'No tax'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={tax.is_active ? 'default' : 'secondary'}>
                        {tax.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                          onClick={() => handleOpenDialog(tax)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedTax(tax); setDeleteDialogOpen(true); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800">
          <DialogHeader>
            <DialogTitle>{selectedTax ? 'Edit Tax Group' : 'Add Tax Group'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Code *</Label>
                <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., GST18" className="bg-gray-50 border-gray-300" disabled={!!selectedTax} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., GST 18%" className="bg-gray-50 border-gray-300" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-600">Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Tax description" className="bg-gray-50 border-gray-300" />
            </div>

            <div className="space-y-3">
              <Label className="text-gray-600">Tax Rates</Label>
              
              {/* Intra-State (Same State) - CGST + SGST */}
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-blue-500">Intra-State (Same State)</Badge>
                  <span className="text-xs text-blue-600">CGST + SGST applies</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-blue-700 text-xs">CGST Rate (%)</Label>
                    <Input type="number" step="0.01" min="0" max="28" value={formData.slabs[0]?.rate || 0}
                      onChange={(e) => updateSlabRate(0, e.target.value)}
                      className="bg-white border-blue-300 mt-1" />
                  </div>
                  <div>
                    <Label className="text-blue-700 text-xs">SGST Rate (%)</Label>
                    <Input type="number" step="0.01" min="0" max="28" value={formData.slabs[1]?.rate || 0}
                      onChange={(e) => updateSlabRate(1, e.target.value)}
                      className="bg-white border-blue-300 mt-1" />
                  </div>
                </div>
                <div className="text-right mt-2">
                  <span className="text-blue-600 text-sm">Total: </span>
                  <span className="text-blue-700 font-bold">{getIntraStateRate()}%</span>
                </div>
              </div>
              
              {/* Inter-State (Different State) - IGST */}
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-amber-500">Inter-State (Different State)</Badge>
                  <span className="text-xs text-amber-600">IGST applies (auto-calculated)</span>
                </div>
                <div>
                  <Label className="text-amber-700 text-xs">IGST Rate (%) - Auto = CGST + SGST</Label>
                  <Input type="number" step="0.01" min="0" max="56" value={formData.slabs[2]?.rate || 0}
                    onChange={(e) => updateSlabRate(2, e.target.value)}
                    className="bg-white border-amber-300 mt-1" />
                  <p className="text-xs text-amber-600 mt-1">
                    IGST is automatically applied for sales to customers in different states
                  </p>
                </div>
              </div>
              
              <div className="text-right pt-2 border-t border-gray-200">
                <span className="text-gray-500">Display Rate: </span>
                <span className="text-green-500 font-bold text-lg">{getTotalRate()}%</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-500 hover:bg-blue-600">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Tax Group?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate "{selectedTax?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxPage;
